import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronLeft, Cpu, Wrench, Factory, X, ZoomIn } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightbox Component
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
          onClick={onClose}
        >
          <X size={24} className="text-white" />
        </motion.button>
        
        <motion.img
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          src={image}
          alt="Enlarged view"
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Image Carousel Component
const ImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="card-technical p-12 text-center h-80 flex items-center justify-center">
        <p className="text-[#94A3B8] text-lg">Images coming soon...</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      {/* Main Image Display */}
      <div className="relative overflow-hidden border border-[#6B8CAE]/30 bg-white/5 h-80">
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="w-full h-full object-contain cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        />
        
        {/* Zoom Icon Overlay */}
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        >
          <div className="w-12 h-12 bg-[#6B8CAE] flex items-center justify-center">
            <ZoomIn size={24} className="text-white" />
          </div>
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute bottom-3 right-3 bg-[#0F172A]/80 px-3 py-1 text-white text-sm font-mono">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 h-16 border-2 overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'border-[#6B8CAE] opacity-100' 
                  : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EngineeringPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rnd');
  const [lightboxImage, setLightboxImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    en: {
      back: 'Back to Services',
      title: 'Engineering',
      description: 'Engineering is the foundation of every successful product, machine and production system. For manufacturers, machine builders and product development teams, strong engineering means lower development risk, fewer costly changes at a later stage and a smoother path from concept to production. We support projects from the early development phase to production-ready documentation, with a clear focus on functionality, manufacturability and long-term reliability.',
      clickToZoom: 'Click image to enlarge',
      tabs: {
        rnd: {
          title: 'R&D Engineering',
          items: [
            'Concept development',
            '3D modeling and 2D drawings',
            'Development of complex machinery and products',
            'Engineering calculations and simulations',
            'Preparation of documentation for serial production',
            'Product lifecycle support'
          ]
        },
        manufacturing: {
          title: 'Manufacturing Engineering',
          items: [
            'Preparation of assembly diagrams and material flow documentation',
            'Definition and optimization of manufacturing processes',
            'Assembly instructions and quality control documentation',
            'Production layout planning for all workstations',
            'KIT assembly planning'
          ]
        },
        industrial: {
          title: 'Industrial Equipment',
          items: [
            'Welding fixture development',
            'Gluing fixture development',
            'Quality control fixture development',
            'Cutting fixture development'
          ]
        }
      }
    },
    bg: {
      back: 'Обратно към Услуги',
      title: 'Инженеринг',
      description: 'Инженерингът е основата на всеки успешен продукт, машина и производствена система. За производителите, машиностроителите и екипите за разработка на продукти, силното инженерство означава по-нисък риск при разработката, по-малко скъпи промени на по-късен етап и по-гладък път от концепцията до производството. Ние подкрепяме проекти от ранната фаза на разработка до документация, готова за производство, с ясен фокус върху функционалността, производимостта и дългосрочната надеждност.',
      clickToZoom: 'Кликнете върху изображението за увеличаване',
      tabs: {
        rnd: {
          title: 'R&D Инженеринг',
          items: [
            'Разработка на концепция',
            '3D моделиране и 2D чертежи',
            'Разработка на сложни машини и продукти',
            'Инженерни изчисления и симулации',
            'Подготовка на документация за серийно производство',
            'Поддръжка на жизнения цикъл на продукта'
          ]
        },
        manufacturing: {
          title: 'Производствен инженеринг',
          items: [
            'Подготовка на монтажни схеми и документация за материален поток',
            'Дефиниране и оптимизация на производствени процеси',
            'Инструкции за монтаж и документация за контрол на качеството',
            'Планиране на производствено оформление за всички работни станции',
            'KIT планиране на монтаж'
          ]
        },
        industrial: {
          title: 'Индустриално оборудване',
          items: [
            'Разработка на заваръчни приспособления',
            'Разработка на лепящи приспособления',
            'Разработка на приспособления за контрол на качеството',
            'Разработка на режещи приспособления'
          ]
        }
      }
    }
  };

  const t = texts[language] || texts.en;

  const tabImages = {
    rnd: [
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/bgrpzuo4_Picture1.jpg',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/2nxhr5cs_Picture3.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/zahurzpa_Picture4.png'
    ],
    manufacturing: [
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/q3c90k8x_Picture7.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/q0m2cv8b_Picture8.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/0v27o8xn_Picture9.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/a09d2xh0_Picture10.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/55o14ah1_Picture11.png'
    ],
    industrial: [
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/a6g36ova_Picture14.png',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/cw9m9gv8_Picture22.jpg',
      'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/dqi1djsl_Picture23.jpg'
    ]
  };

  const tabIcons = {
    rnd: Cpu,
    manufacturing: Factory,
    industrial: Wrench
  };

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <Navbar />
      
      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#6B8CAE] hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm uppercase tracking-wider">{t.back}</span>
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center">
                <Cpu size={32} className="text-[#6B8CAE]" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white" data-testid="engineering-page-title">
                {t.title}
              </h1>
            </div>
            <p className="text-[#94A3B8] text-lg leading-relaxed max-w-4xl">
              {t.description}
            </p>
          </motion.div>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap gap-4 mb-12"
          >
            {Object.keys(t.tabs).map((tabKey) => {
              const TabIcon = tabIcons[tabKey];
              return (
                <button
                  key={tabKey}
                  onClick={() => setActiveTab(tabKey)}
                  className={`flex items-center space-x-2 px-6 py-4 border transition-all ${
                    activeTab === tabKey
                      ? 'bg-[#6B8CAE]/20 border-[#6B8CAE] text-white'
                      : 'bg-transparent border-[#6B8CAE]/30 text-[#94A3B8] hover:border-[#6B8CAE]/50'
                  }`}
                  data-testid={`engineering-tab-${tabKey}`}
                >
                  <TabIcon size={20} />
                  <span className="font-medium">{t.tabs[tabKey].title}</span>
                </button>
              );
            })}
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid lg:grid-cols-2 gap-12"
          >
            {/* Items List */}
            <div className="card-technical p-8">
              <h2 className="text-2xl font-semibold text-white mb-8 flex items-center space-x-3">
                {React.createElement(tabIcons[activeTab], { size: 24, className: 'text-[#6B8CAE]' })}
                <span>{t.tabs[activeTab].title}</span>
              </h2>
              <ul className="space-y-4">
                {t.tabs[activeTab].items.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start space-x-3 text-[#94A3B8]"
                  >
                    <ChevronRight size={20} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Images Carousel */}
            <div>
              <ImageCarousel 
                images={tabImages[activeTab]} 
                onImageClick={(img) => setLightboxImage(img)}
              />
              {tabImages[activeTab].length > 0 && (
                <p className="text-[#6B8CAE] text-sm mt-3 text-center font-mono">
                  <ZoomIn size={14} className="inline mr-1" />
                  {t.clickToZoom}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EngineeringPage;
